import { Body, Box, SimpleGrid, system } from '@hover/blueprint';
import type { FlexProps } from '@hover/blueprint/chakra';
import { Flex } from '@hover/blueprint/chakra';
import type { FunctionComponent } from 'react';
import React, { useContext } from 'react';

import { selectionStyles } from 'src/modules/designStudio/components/SelectionButton';
import type { TemplatePaletteConfig } from 'src/modules/designStudio/helpers/data/PaletteStyles';
import type { Textures } from 'src/modules/designStudio/machines/productCatalog';
import { productCatalogContext } from 'src/modules/designStudio/providers';
import type { ProductEntry } from 'src/modules/designStudio/types';

const getTextureSrcFromId = (
  textures: Textures,
  id: number | undefined | null,
): string | undefined => {
  const texture = textures?.find(t => t.id === id) as ProductEntry;
  return texture ? texture.texture?.thumbnail.url : '';
};

interface PaletteCardProps extends FlexProps {
  palette: TemplatePaletteConfig;
  onClick: () => void;
  showName: boolean;
  isSelected: boolean;
  ratio?: number;
}

/**
 * @description
 * We expect every palette card to have 4 parts:
 * 1.) Roof img: texture & color
 * 2.) Primary wall img: just color
 * 3.) Trim img: just color
 * 4.) 2nd Wall img, may or may not be the same as the primary wall: just color
 * @param props
 * @constructor
 */
export const PaletteCard: FunctionComponent<PaletteCardProps> = ({
  palette,
  onClick,
  showName,
  isSelected,
  ratio = 1.44,
  ...props
}) => {
  const { textures } = useContext(productCatalogContext);
  if (!palette.wallAppearances) {
    return null;
  }

  const roofSrc = getTextureSrcFromId(
    textures,
    palette.roofAppearances![0].textureId,
  );

  const primaryWallColor = palette.wallAppearances[0].walls.color;
  const trimColor = palette.wallAppearances[0]?.trims?.color;
  const paletteName = palette.paletteName || '';
  let secondaryWallColor = palette.wallAppearances.find(
    a => a.walls.color !== primaryWallColor,
  )?.walls.color;
  if (secondaryWallColor === undefined) {
    secondaryWallColor = primaryWallColor;
  }

  const roofBackground = roofSrc
    ? `url('${roofSrc}') center center`
    : '#3d3d3d';

  return (
    <Flex
      className="palette-item"
      flexDirection="column"
      gap="200"
      {...props}
      position="relative"
    >
      <system.button
        {...selectionStyles({
          isSelected,
          // NOTE: for some reason applying this via `_before` clobbers other styles
          sx: {
            '&:before': {
              height: 0,
              content: `""`,
              display: 'block',
              paddingBottom: `${(1 / ratio) * 100}%}`,
            },
          },
        })}
        _focusVisible={{ outline: 'none' }}
        borderRadius={300}
        cursor="pointer"
        height="100%"
        onClick={onClick}
        position="relative"
        width="100%"
      >
        <SimpleGrid
          borderRadius={300}
          bottom="0"
          flexGrow="0"
          flexShrink="0"
          gridColumnGap="2px"
          gridRowGap="2px"
          gridTemplateColumns="repeat(4 1fr)"
          gridTemplateRows="repeat(3 1fr)"
          left="0"
          overflow="hidden"
          position="absolute"
          right="0"
          top="0"
        >
          <Box
            background={roofBackground}
            backgroundSize="cover"
            gridArea="1 / 1 / 2 / 5"
            height="100%"
            objectFit="cover"
            width="100%"
          />
          <Box
            backgroundColor={primaryWallColor ?? '#ffffff'}
            gridArea="2 / 1 / 4 / 3"
          />
          <Box
            backgroundColor={trimColor ?? '#ffffff'}
            gridArea="2 / 3 / 3 / 5"
          />
          <Box
            backgroundColor={secondaryWallColor ?? '#ffffff'}
            gridArea="3 / 3 / 4 / 5"
          />
        </SimpleGrid>
      </system.button>
      {showName && (
        <Body className="my-custom" m={0}>
          {paletteName}
        </Body>
      )}
    </Flex>
  );
};
